import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import bgimg from "../../../../assets/banner-1.png";
import logo from "../../../../assets/Easymarg logo - Copy.png";

const HeroSection = () => {
    const [localSearchTerm, setLocalSearchTerm] = useState("");
    const navigate = useNavigate();

    const handleSearchSubmit = (e) => {
        e.preventDefault(); 
        
        const searchTerm = localSearchTerm.trim();
        if (!searchTerm) {
            navigate(`/services`);
            return;
        }
        
        // Navigate to services page with search query in URL
        navigate(`/services?q=${encodeURIComponent(searchTerm)}`);
    };

    // ** POPULATED STATS ARRAY ** (Remains the same)
    const stats = [
       
    ];

    return (
        <section className="relative w-full min-h-screen flex flex-col justify-center items-center overflow-hidden">
            {/* Background and Overlays */}
            <div
                className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url(${bgimg})` }}
            >
                <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/70 to-black/60"></div>
                <div className="absolute inset-0 bg-gradient-to-tr from-[#134DA7]/30 via-transparent to-[#0097b2]/30"></div>
            </div>


            <div className="relative z-10 px-4 max-w-6xl mx-auto text-center pt-32 md:pt-40 pb-20 w-full mt-8 md:mt-12">

                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    className="mb-8"
                >
                    <img
                        src={logo}
                        alt="EasyMarg Logo"
                        className="h-20 md:h-24 mx-auto drop-shadow-2xl"
                    />
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.6 }}
                >
                    <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight drop-shadow-lg">
                        Welcome to <span className="bg-gradient-to-r from-[#0097b2] to-[#66BBFC] bg-clip-text text-transparent">EasyMarg</span>
                    </h1>

                    <p className="text-xl md:text-2xl text-gray-200 mb-12 max-w-3xl mx-auto leading-relaxed drop-shadow-md">
                        Your trusted partner for professional financial, legal, and business services.
                        Connect with verified experts and achieve your goals.
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.6 }}
                    className="max-w-xl mx-auto mb-10"
                >
                    <form onSubmit={handleSearchSubmit} className="relative">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                            type="text"
                            placeholder="Search services or experts..."
                            value={localSearchTerm}
                            onChange={(e) => setLocalSearchTerm(e.target.value)}
                            className="w-full pl-10 md:pl-12 pr-24 md:pr-28 py-3 md:py-3.5 rounded-xl border-2 border-white/20 bg-white/95 backdrop-blur-md focus:border-[#0097b2] focus:ring-4 focus:ring-[#0097b2]/30 outline-none transition-all text-gray-700 text-sm md:text-base shadow-2xl placeholder:text-gray-500"
                        />
                        <button 
                            type="submit"
                            className="absolute right-1.5 top-1/2 transform -translate-y-1/2 px-4 md:px-5 py-1.5 md:py-2 bg-gradient-to-r from-[#134DA7] to-[#0097b2] text-white text-xs md:text-sm font-semibold rounded-lg hover:from-[#0f3d8a] hover:to-[#007a94] transition-all shadow-lg hover:shadow-xl"
                        >
                            Search
                        </button>
                    </form>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.6 }}
                    className="flex flex-wrap justify-center gap-3 mb-12"
                >
                    <Link
                        to="/services"
                        className="px-6 py-2.5 text-sm bg-white/10 backdrop-blur-md text-white font-semibold rounded-lg hover:bg-white/20 transition-all shadow-lg border border-white/20 hover:border-white/40"
                    >
                        Browse Services
                    </Link>
                    <Link
                        to="/our-experts"
                        className="px-6 py-2.5 text-sm bg-gradient-to-r from-[#134DA7] to-[#0097b2] text-white font-semibold rounded-lg hover:from-[#0f3d8a] hover:to-[#007a94] transition-all shadow-xl hover:shadow-2xl hover:shadow-[#0097b2]/50"
                    >
                        Find Experts
                    </Link>
                    <Link
                        to="/categories"
                        className="px-6 py-2.5 text-sm bg-white/10 backdrop-blur-md text-white font-semibold rounded-lg hover:bg-white/20 transition-all shadow-lg border border-white/20 hover:border-white/40"
                    >
                        View Categories
                    </Link>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8, duration: 0.6 }}
                    className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto"
                >
                    {stats.map((stat, index) => (
                        <div
                            key={index}
                            className="bg-white/10 backdrop-blur-md rounded-2xl p-6 shadow-xl border border-white/20 hover:bg-white/15 transition-all"
                        >
                            <div className="flex items-center justify-center gap-3 mb-2">
                                <div className="text-[#66BBFC]">{stat.icon}</div>
                                <div className="text-3xl font-bold text-white">{stat.value}</div>
                            </div>
                            <div className="text-gray-300 font-medium">{stat.label}</div>
                        </div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

export default HeroSection;